import {
  ALIEN_CONFIG,
  BULLET_CONFIG,
  DIFFICULTY_CONFIG,
} from '../config/game.config'
import {
  playerSkillProfile,
  GamePerformanceRecord,
  SkillProfile,
} from './PlayerSkillProfile'

export interface DifficultyModifiers {
  alienSpeed: number
  alienShootIntervalMin: number
  alienShootIntervalMax: number
  alienBulletSpeed: number
  playerBulletCooldown: number
}

export interface GameMetrics {
  shotsFired: number
  shotsHit: number
  aliensDestroyed: number
  totalAliens: number
  gameStartTime: number
  currentTime: number
  score: number
}

export class DifficultyManager {
  private metrics: GameMetrics
  private currentModifiers: DifficultyModifiers
  private baseModifiers: DifficultyModifiers
  private skillLevel: SkillProfile['skillLevel']

  constructor() {
    this.skillLevel = playerSkillProfile.getSkillLevel()
    this.baseModifiers = this.calculateBaseModifiers()
    this.currentModifiers = { ...this.baseModifiers }
    this.metrics = this.createInitialMetrics()
  }

  private createInitialMetrics(): GameMetrics {
    return {
      shotsFired: 0,
      shotsHit: 0,
      aliensDestroyed: 0,
      totalAliens: ALIEN_CONFIG.rows * ALIEN_CONFIG.cols,
      gameStartTime: Date.now(),
      currentTime: Date.now(),
      score: 0,
    }
  }

  private calculateBaseModifiers(): DifficultyModifiers {
    const levelMods = DIFFICULTY_CONFIG.modifiers[this.skillLevel]

    return {
      alienSpeed: ALIEN_CONFIG.initialSpeed * levelMods.alienSpeed,
      alienShootIntervalMin: ALIEN_CONFIG.shootInterval.min * levelMods.alienShootIntervalMultiplier,
      alienShootIntervalMax: ALIEN_CONFIG.shootInterval.max * levelMods.alienShootIntervalMultiplier,
      alienBulletSpeed: BULLET_CONFIG.alienBullet.speed * levelMods.alienBulletSpeed,
      playerBulletCooldown: BULLET_CONFIG.playerBullet.cooldown * levelMods.playerBulletCooldown,
    }
  }

  // Called when game starts or restarts
  resetForNewGame(): void {
    this.skillLevel = playerSkillProfile.getSkillLevel()
    this.baseModifiers = this.calculateBaseModifiers()
    this.currentModifiers = { ...this.baseModifiers }
    this.metrics = this.createInitialMetrics()
  }

  // Track a player shot fired
  recordShotFired(): void {
    this.metrics.shotsFired++
    this.updateAdaptiveDifficulty()
  }

  // Track a shot that hit an alien
  recordShotHit(points: number): void {
    this.metrics.shotsHit++
    this.metrics.aliensDestroyed++
    this.metrics.score += points
    this.updateAdaptiveDifficulty()
  }

  // Update time for survival tracking
  updateTime(): void {
    this.metrics.currentTime = Date.now()
  }

  // Get current difficulty modifiers
  getModifiers(): DifficultyModifiers {
    return { ...this.currentModifiers }
  }

  // Get initial alien speed (for game start)
  getInitialAlienSpeed(): number {
    return this.currentModifiers.alienSpeed
  }

  // Get alien shoot interval range
  getAlienShootInterval(): { min: number; max: number } {
    return {
      min: Math.round(this.currentModifiers.alienShootIntervalMin),
      max: Math.round(this.currentModifiers.alienShootIntervalMax),
    }
  }

  // Get alien bullet speed
  getAlienBulletSpeed(): number {
    return this.currentModifiers.alienBulletSpeed
  }

  // Get player bullet cooldown
  getPlayerBulletCooldown(): number {
    return Math.round(this.currentModifiers.playerBulletCooldown)
  }

  // Get current skill level for display
  getSkillLevel(): SkillProfile['skillLevel'] {
    return this.skillLevel
  }

  // Get current metrics for display/debug
  getMetrics(): GameMetrics {
    return { ...this.metrics }
  }

  // Calculate current accuracy
  getCurrentAccuracy(): number {
    if (this.metrics.shotsFired === 0) return 0
    return (this.metrics.shotsHit / this.metrics.shotsFired) * 100
  }

  // Calculate current score per minute
  getCurrentScorePerMinute(): number {
    const elapsedMinutes = this.getElapsedTimeMs() / 60000
    if (elapsedMinutes === 0) return 0
    return this.metrics.score / elapsedMinutes
  }

  // Get elapsed game time in milliseconds
  getElapsedTimeMs(): number {
    return this.metrics.currentTime - this.metrics.gameStartTime
  }

  // Adaptive difficulty adjustment during gameplay
  private updateAdaptiveDifficulty(): void {
    // Only adapt if we have enough data
    if (this.metrics.shotsFired < 5) return

    const accuracy = this.getCurrentAccuracy()
    const scorePerMinute = this.getCurrentScorePerMinute()
    const destructionRatio = (this.metrics.aliensDestroyed / this.metrics.totalAliens) * 100

    // Calculate a "performance index" for this game session (0-100)
    const performanceIndex =
      accuracy * DIFFICULTY_CONFIG.weights.accuracy +
      Math.min(100, scorePerMinute / 3) * DIFFICULTY_CONFIG.weights.scorePerMinute +
      destructionRatio * DIFFICULTY_CONFIG.weights.aliensDestroyedRatio

    // Determine if player is over/under-performing relative to their skill level
    const skillScore = playerSkillProfile.getSkillScore()
    const performanceDelta = performanceIndex - skillScore

    // Apply micro-adjustments based on performance delta
    // If performing much better than profile: slight difficulty increase
    // If performing much worse: slight difficulty decrease
    const adjustmentFactor = 1 + (performanceDelta / 100) * DIFFICULTY_CONFIG.adaptation.adjustmentRate

    // Clamp adjustment factor to prevent extreme changes
    const clampedFactor = Math.max(0.85, Math.min(1.15, adjustmentFactor))

    // Apply adjustments (only to speed-related parameters for mid-game)
    // We don't adjust shoot intervals mid-game as it would be disorienting
    this.currentModifiers.alienSpeed = this.baseModifiers.alienSpeed * clampedFactor
    this.currentModifiers.alienBulletSpeed = this.baseModifiers.alienBulletSpeed * clampedFactor
  }

  // Call at game end to record performance
  recordGameEnd(won: boolean): void {
    this.updateTime()

    const record: GamePerformanceRecord = {
      timestamp: Date.now(),
      accuracy: this.getCurrentAccuracy(),
      survivalTimeMs: this.getElapsedTimeMs(),
      score: this.metrics.score,
      scorePerMinute: this.getCurrentScorePerMinute(),
      aliensDestroyed: this.metrics.aliensDestroyed,
      totalAliens: this.metrics.totalAliens,
      aliensDestroyedRatio: (this.metrics.aliensDestroyed / this.metrics.totalAliens) * 100,
      won,
    }

    playerSkillProfile.recordGamePerformance(record)
  }

  // Get formatted skill info for UI display
  getSkillDisplay(): { level: string; score: number; gamesPlayed: number } {
    const profile = playerSkillProfile.getProfile()
    return {
      level: this.formatSkillLevel(profile.skillLevel),
      score: Math.round(profile.skillScore),
      gamesPlayed: profile.gamesPlayed,
    }
  }

  private formatSkillLevel(level: SkillProfile['skillLevel']): string {
    const levelNames: Record<SkillProfile['skillLevel'], string> = {
      beginner: 'Beginner',
      intermediate: 'Intermediate',
      advanced: 'Advanced',
      expert: 'Expert',
    }
    return levelNames[level]
  }
}

// Singleton instance
export const difficultyManager = new DifficultyManager()
